import React, { useState, useEffect } from 'react';
import { useEffectiveUser } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import ForumSidePanel from '../components/CommunityForum/ForumSidePanel';
import ForumMainContent from '../components/CommunityForum/ForumMainContent';
import ForumPostingArea from '../components/CommunityForum/ForumPostingArea';
import { db } from '../firebase';
import { collection, query, orderBy, limit, getDocs, startAfter, where, onSnapshot, getDoc, doc } from 'firebase/firestore';
import { motion } from 'framer-motion';
import { FaUsers, FaComments, FaChartLine, FaCalendarWeek } from 'react-icons/fa';

function CommunityForum() {
  const { currentUser } = useEffectiveUser();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [commentCounts, setCommentCounts] = useState({});
  const [sortOrder, setSortOrder] = useState('recent');
  const [refreshKey, setRefreshKey] = useState(0);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [forumStats, setForumStats] = useState({
    totalPosts: 0,
    totalComments: 0,
    activeUsers: 0,
    weeklyPosts: 0
  });

  // Check forum agreement before allowing access
  useEffect(() => {
    const checkForumAgreement = async () => {
      if (!currentUser) return;
      
      try {
        const agreementDoc = await getDoc(doc(db, 'forumAgreement', currentUser.uid));
        
        if (!agreementDoc.exists() || !agreementDoc.data().hasAccepted) {
          // User hasn't accepted the agreement, redirect to agreement page
          navigate('/forum/agreement');
          return;
        }
        
        // User has accepted, allow access to forum
        setAgreementChecked(true);
      } catch (error) {
        console.error('Error checking forum agreement:', error);
        // On error, redirect to dashboard for safety
        navigate('/dashboard');
      }
    };

    checkForumAgreement();
  }, [currentUser, navigate]);

  useEffect(() => {
    if (!currentUser || !agreementChecked) return;

    // Listen for comments to update comment counts
    const commentsQuery = query(
      collection(db, 'comments')
    );

    const unsubscribeComments = onSnapshot(commentsQuery, (snapshot) => {
      const counts = {};
      
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.postId && data.comments && Array.isArray(data.comments)) {
          counts[data.postId] = (counts[data.postId] || 0) + data.comments.length;
        }
      });
      
      setCommentCounts(counts);
    }, (error) => {
      console.error('Error fetching comment counts:', error);
    });

    return () => {
      unsubscribeComments();
    };
  }, [currentUser, agreementChecked]);

  // Function to set up real-time listener for posts
  useEffect(() => {
    if (!currentUser || !agreementChecked) return;
    
    let postsQuery;
    let unsubscribeFunction;
    
    if (selectedLabel === 'following') {
      // Special handling for following posts
      const setupFollowingPosts = async () => {
        try {
          const followingDoc = await getDoc(doc(db, 'followingPosts', currentUser.uid));
          if (followingDoc.exists()) {
            const followingData = followingDoc.data();
            const followingPostIds = followingData.posts || [];
            
            if (followingPostIds.length > 0) {
              // Set up listener for all posts, then filter
              const allPostsQuery = query(
                collection(db, 'posts'),
                where('postStatus', '!=', 'removed'),
                orderBy('postStatus'),
                orderBy('createdAt', 'desc')
              );
              
              unsubscribeFunction = onSnapshot(allPostsQuery, (snapshot) => {
                const allPosts = snapshot.docs.map(doc => ({
                  id: doc.id,
                  ...doc.data(),
                  commentCount: doc.data().commentCount || commentCounts[doc.id] || 0
                }));
                
                // Filter to only include posts the user is following
                const followingPosts = allPosts.filter(post => 
                  followingPostIds.includes(post.id)
                );
                
                setPosts(followingPosts);
                setLastVisible(null);
                setHasMore(false);
                setLoading(false);
              });
            } else {
              // User isn't following any posts
              setPosts([]);
              setLastVisible(null);
              setHasMore(false);
              setLoading(false);
            }
          } else {
            // User has no following document
            setPosts([]);
            setLastVisible(null);
            setHasMore(false);
            setLoading(false);
          }
        } catch (error) {
          console.error('Error fetching following posts:', error);
          setPosts([]);
          setLastVisible(null);
          setHasMore(false);
          setLoading(false);
        }
      };
      
      setLoading(true);
      setupFollowingPosts();
      
      return () => {
        if (unsubscribeFunction) {
          unsubscribeFunction();
        }
      };
    }
    
    // Handle other label types (admin, regular labels, or no filter)
    if (sortOrder === 'recent') {
      // Recent posts - Sort by creation date
      if (selectedLabel === 'admin') {
        // Special case for admin posts
        postsQuery = query(
          collection(db, 'posts'),
          where('isAdmin', '==', true),
          where('postStatus', '!=', 'removed'),
          orderBy('postStatus'),
          orderBy('createdAt', 'desc'),
          limit(10)
        );
      } else if (selectedLabel === 'following') {
        // This case is handled above
        return;
      } else if (selectedLabel) {
        postsQuery = query(
          collection(db, 'posts'),
          where('postLabels', 'array-contains', selectedLabel),
          where('postStatus', '!=', 'removed'),
          orderBy('postStatus'),
          orderBy('createdAt', 'desc'),
          limit(10)
        );
      } else {
        postsQuery = query(
          collection(db, 'posts'),
          where('postStatus', '!=', 'removed'),
          orderBy('postStatus'),
          orderBy('createdAt', 'desc'),
          limit(10)
        );
      }
    } else if (sortOrder === 'popular') {
      // Popular posts - Sort by upvotes
      if (selectedLabel === 'admin') {
        // Special case for admin posts
        postsQuery = query(
          collection(db, 'posts'),
          where('isAdmin', '==', true),
          where('postStatus', '!=', 'removed'),
          orderBy('postStatus'),
          orderBy('upvoteCount', 'desc'),
          limit(10)
        );
      } else if (selectedLabel) {
        postsQuery = query(
          collection(db, 'posts'),
          where('postLabels', 'array-contains', selectedLabel),
          where('postStatus', '!=', 'removed'),
          orderBy('postStatus'),
          orderBy('upvoteCount', 'desc'),
          limit(10)
        );
      } else {
        postsQuery = query(
          collection(db, 'posts'),
          where('postStatus', '!=', 'removed'),
          orderBy('postStatus'),
          orderBy('upvoteCount', 'desc'),
          limit(10)
        );
      }
    }

    setLoading(true);
    
    const unsubscribePosts = onSnapshot(postsQuery, (snapshot) => {
      const fetchedPosts = snapshot.docs.map(doc => {
        const postData = doc.data();
        return {
          id: doc.id,
          ...postData,
          // If the post doesn't have a commentCount field, use our real-time count
          commentCount: postData.commentCount || commentCounts[doc.id] || 0
        };
      });
      
      setPosts(fetchedPosts);
      setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
      setHasMore(snapshot.docs.length === 10);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching posts:', error);
      setLoading(false);
    });
    
    return () => {
      unsubscribePosts();
    };
  }, [currentUser, agreementChecked, selectedLabel, commentCounts, sortOrder, refreshKey]);

  // Calculate forum stats from ALL posts (not filtered)
  useEffect(() => {
    if (!currentUser || !agreementChecked) return;

    // Listen to ALL posts for stats calculation
    const allPostsQuery = query(
      collection(db, 'posts'),
      where('postStatus', '!=', 'removed')
    );

    const unsubscribeAllPosts = onSnapshot(allPostsQuery, (snapshot) => {
      const allPosts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const totalComments = Object.values(commentCounts).reduce((sum, count) => sum + count, 0);
      const uniqueUsers = new Set(allPosts.map(post => post.userId)).size;

      // Calculate posts from the last 7 days
      const now = new Date();
      const weekAgo = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000)); // 7 days ago
      
      const weeklyPostsCount = allPosts.filter(post => {
        if (!post.createdAt) return false;
        
        const postDate = post.createdAt.toDate ? post.createdAt.toDate() : new Date(post.createdAt);
        return postDate >= weekAgo && postDate <= now;
      }).length;

      setForumStats({
        totalPosts: allPosts.length,
        totalComments,
        activeUsers: uniqueUsers,
        weeklyPosts: weeklyPostsCount
      });
    }, (error) => {
      console.error('Error fetching all posts for stats:', error);
    });

    return () => {
      unsubscribeAllPosts();
    };
  }, [currentUser, agreementChecked, commentCounts]);

  // Don't render forum content until agreement is checked
  if (!agreementChecked) {
    return (
      <div className="min-h-screen bg-blue-50 dark:bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-opacity-30 border-t-purple-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Checking forum access...</p>
        </div>
      </div>
    );
  }

  const loadMorePosts = async () => {
    if (!hasMore || !lastVisible) return;

    // Following posts don't support pagination in this implementation
    if (selectedLabel === 'following') {
      return;
    }

    try {
      setLoading(true);
      let postsQuery;
      
      if (sortOrder === 'recent') {
        // Recent posts - Sort by creation date
        if (selectedLabel === 'admin') {
          // Special case for admin posts
          postsQuery = query(
            collection(db, 'posts'),
            where('isAdmin', '==', true),
            where('postStatus', '!=', 'removed'),
            orderBy('postStatus'),
            orderBy('createdAt', 'desc'),
            startAfter(lastVisible),
            limit(10)
          );
        } else if (selectedLabel) {
          postsQuery = query(
            collection(db, 'posts'),
            where('postLabels', 'array-contains', selectedLabel),
            where('postStatus', '!=', 'removed'),
            orderBy('postStatus'),
            orderBy('createdAt', 'desc'),
            startAfter(lastVisible),
            limit(10)
          );
        } else {
          postsQuery = query(
            collection(db, 'posts'),
            where('postStatus', '!=', 'removed'),
            orderBy('postStatus'),
            orderBy('createdAt', 'desc'),
            startAfter(lastVisible),
            limit(10)
          );
        }
      } else if (sortOrder === 'popular') {
        // Popular posts - Sort by upvotes
        if (selectedLabel === 'admin') {
          // Special case for admin posts
          postsQuery = query(
            collection(db, 'posts'),
            where('isAdmin', '==', true),
            where('postStatus', '!=', 'removed'),
            orderBy('postStatus'),
            orderBy('upvoteCount', 'desc'),
            startAfter(lastVisible),
            limit(10)
          );
        } else if (selectedLabel) {
          postsQuery = query(
            collection(db, 'posts'),
            where('postLabels', 'array-contains', selectedLabel),
            where('postStatus', '!=', 'removed'),
            orderBy('postStatus'),
            orderBy('upvoteCount', 'desc'),
            startAfter(lastVisible),
            limit(10)
          );
        } else {
          postsQuery = query(
            collection(db, 'posts'),
            where('postStatus', '!=', 'removed'),
            orderBy('postStatus'),
            orderBy('upvoteCount', 'desc'),
            startAfter(lastVisible),
            limit(10)
          );
        }
      }

      const querySnapshot = await getDocs(postsQuery);
      const newPosts = querySnapshot.docs.map(doc => {
        const postData = doc.data();
        return {
          id: doc.id,
          ...postData,
          commentCount: postData.commentCount || commentCounts[doc.id] || 0
        };
      });

      setPosts(prevPosts => [...prevPosts, ...newPosts]);
      setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
      setHasMore(querySnapshot.docs.length === 10);
    } catch (error) {
      console.error('Error loading more posts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Remove fetchPosts since we're using onSnapshot now
  const refreshPosts = () => {
    setRefreshKey(prev => prev + 1);
  };
  
  // Handle sort order change from ForumMainContent
  const handleSortOrderChange = (newSortOrder) => {
    setSortOrder(newSortOrder);
  };

  return (
    <div className="min-h-screen bg-violet-50/50 dark:bg-gray-700 text-white">
      <Header />
      
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <h1 className="text-2xl md:text-4xl min-h-12 font-bold bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent">
            Community Forum
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Share your thoughts, ask questions, and get help from the community
          </p>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <motion.div 
            className="relative overflow-hidden bg-gradient-to-br from-emerald-700 to-teal-800 rounded-xl p-3 shadow-md"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="relative z-10 flex items-center justify-between">
              <div>
                <h3 className="text-xs text-emerald-100 font-medium">Total Posts</h3>
                <p className="text-xl font-bold text-white mt-0.5">
                  {forumStats.totalPosts}
                </p>
              </div>
              <FaComments className="w-6 h-6 text-white opacity-80" />
            </div>
          </motion.div>

          <motion.div 
            className="relative overflow-hidden bg-gradient-to-br from-violet-700 to-purple-800 rounded-xl p-3 shadow-md"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="relative z-10 flex items-center justify-between">
              <div>
                <h3 className="text-xs text-violet-100 font-medium">Total Comments</h3>
                <p className="text-xl font-bold text-white mt-0.5">
                  {forumStats.totalComments}
                </p>
              </div>
              <FaUsers className="w-6 h-6 text-white opacity-80" />
            </div>
          </motion.div>

          <motion.div 
            className="relative overflow-hidden bg-gradient-to-br from-amber-700 to-orange-800 rounded-xl p-3 shadow-md"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="relative z-10 flex items-center justify-between">
              <div>
                <h3 className="text-xs text-amber-100 font-medium">Active Users</h3>
                <p className="text-xl font-bold text-white mt-0.5">
                  {forumStats.activeUsers}
                </p>
              </div>
              <FaChartLine className="w-6 h-6 text-white opacity-80" />
            </div>
          </motion.div>

          <motion.div 
            className="relative overflow-hidden bg-gradient-to-br from-blue-700 to-cyan-800 rounded-xl p-3 shadow-md"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="relative z-10 flex items-center justify-between">
              <div>
                <h3 className="text-xs text-blue-100 font-medium">This Week's Posts</h3>
                <p className="text-xl font-bold text-white mt-0.5">
                  {forumStats.weeklyPosts}
                </p>
              </div>
              <FaCalendarWeek className="w-6 h-6 text-white opacity-80" />
            </div>
          </motion.div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Side Panel */}
          <div className="w-full lg:w-1/4">
            <ForumSidePanel 
              selectedLabel={selectedLabel}
              onLabelSelect={(labelId) => {
                setSelectedLabel(labelId);
              }}
            />
          </div>

          {/* Main Content Area */}
          <div className="w-full lg:w-3/4 space-y-6">
            <ForumPostingArea onPostCreated={refreshPosts} />
            <ForumMainContent 
              posts={posts}
              loading={loading}
              hasMore={hasMore}
              onLoadMore={loadMorePosts}
              onPostUpdated={refreshPosts}
              selectedLabel={selectedLabel}
              sortOrder={sortOrder}
              onSortOrderChange={handleSortOrderChange}
            />
          </div>
        </div>
      </main>
    </div>
  );
}

export default CommunityForum;
